"use client";

import { useState, useRef, type FormEvent, type RefObject } from "react";
import dynamic from "next/dynamic";
import Link from "next/link";
import {
  calculateMatching,
  type PersonInput,
  type MatchingResponse,
  type PlaceResult,
} from "@/lib/api";
import Input from "@ishvaram/ui/Input";
import Button from "@ishvaram/ui/Button";
import PlaceAutocomplete from "@/components/PlaceAutocomplete";
import {
  trackAgentRevenueOfferShown,
  trackCalculatorUsed,
  trackMatchingCompleted,
} from "@/lib/analytics";
import { useImpressionOnce } from "@/hooks/useImpressionOnce";
import SavedLinkCard from "@/components/SavedLinkCard";
import { ValueMomentContactCapture } from "@/components/ValueMomentContactCapture";

// Post-submission result components — loaded only when result arrives
const MatchingScoreCard = dynamic(
  () => import("@/components/matching/MatchingScoreCard").then((m) => m.MatchingScoreCard),
  { ssr: false, loading: () => null }
);
const KootaBreakdown = dynamic(
  () => import("@/components/matching/KootaBreakdown").then((m) => m.KootaBreakdown),
  { ssr: false, loading: () => null }
);
const MangalDoshaStatus = dynamic(
  () => import("@/components/matching/MangalDoshaStatus").then((m) => m.MangalDoshaStatus),
  { ssr: false, loading: () => null }
);
const MatchingCrossSell = dynamic(
  () => import("@/components/matching/MatchingCrossSell").then((m) => m.MatchingCrossSell),
  { ssr: false, loading: () => null }
);
const PaidReportCTA = dynamic(() => import("@/components/PaidReportCTA"), {
  ssr: false,
  loading: () => null,
});
const AIAstrologerCTA = dynamic(() => import("@/components/AIAstrologerCTA"), {
  ssr: false,
  loading: () => null,
});
const RatingPrompt = dynamic(() => import("@/components/RatingPrompt"), {
  ssr: false,
  loading: () => null,
});

interface FormState {
  name: string;
  birthDate: string;
  birthTime: string;
  birthPlace: string;
  place: PlaceResult | null;
}

const empty = (): FormState => ({
  name: "",
  birthDate: "",
  birthTime: "",
  birthPlace: "",
  place: null,
});

function PersonPanel({
  label,
  icon,
  data,
  onChange,
}: {
  label: string;
  icon: string;
  data: FormState;
  onChange: (patch: Partial<FormState>) => void;
}) {
  const today = new Date().toISOString().slice(0, 10);
  const id = label.toLowerCase().replace(/\s+/g, "-");

  return (
    <div className="card-sacred p-5 sm:p-6 space-y-4 flex-1">
      <h3
        className="text-[16px] font-bold flex items-center gap-2"
        style={{ color: "var(--ish-dark)", fontFamily: "PT Serif, Georgia, serif" }}
      >
        <span>{icon}</span> {label}
      </h3>

      <Input
        id={`name-${id}`}
        type="text"
        required
        label="Full Name"
        value={data.name}
        onChangeText={(v) => onChange({ name: v })}
        placeholder="Enter full name"
        autoComplete="name"
        autoCapitalize="words"
      />

      <Input
        id={`date-${id}`}
        type="date"
        required
        label="Birth Date"
        value={data.birthDate}
        onChangeText={(v) => onChange({ birthDate: v })}
        min="1900-01-01"
        max={today}
      />

      <Input
        id={`time-${id}`}
        type="time"
        label="Birth Time (optional)"
        value={data.birthTime}
        onChangeText={(v) => onChange({ birthTime: v })}
      />

      <div>
        <label
          htmlFor={`place-${id}`}
          className="block text-[13px] font-semibold mb-2"
          style={{ color: "var(--ish-dark)" }}
        >
          Birth Place
        </label>
        <PlaceAutocomplete
          id={`place-${id}`}
          value={data.birthPlace}
          onChange={(v) => onChange({ birthPlace: v, place: null })}
          onSelect={(p) => onChange({ birthPlace: p.display, place: p })}
        />
      </div>
    </div>
  );
}

function toPersonInput(f: FormState): PersonInput {
  return {
    name: f.name,
    birth_date: f.birthDate,
    birth_time: f.birthTime || null,
    birth_place: f.birthPlace,
    ...(f.place && {
      latitude: f.place.latitude,
      longitude: f.place.longitude,
      timezone: f.place.timezone,
    }),
  } as PersonInput;
}

export default function GunMilanClient() {
  const [boy, setBoy] = useState<FormState>(empty());
  const [girl, setGirl] = useState<FormState>(empty());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<MatchingResponse | null>(null);
  const resultsRef = useRef<HTMLDivElement>(null);

  async function handleCalculate(e: FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const res = await calculateMatching({
        person_a: toPersonInput(boy),
        person_b: toPersonInput(girl),
      });
      setResult(res);
      trackCalculatorUsed({ calculator: "gun-milan" });
      trackMatchingCompleted({ score: res.total_score ?? 0 });
      setTimeout(
        () => resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }),
        100
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  function handleReset() {
    setBoy(empty());
    setGirl(empty());
    setResult(null);
    setError(null);
  }

  return (
    <div className="mx-auto max-w-3xl px-4 sm:px-6 py-8 sm:py-12">
      {!result ? (
        <GunMilanForm
          boy={boy}
          girl={girl}
          loading={loading}
          error={error}
          onSubmit={handleCalculate}
          onBoyChange={(patch) => setBoy((prev) => ({ ...prev, ...patch }))}
          onGirlChange={(patch) => setGirl((prev) => ({ ...prev, ...patch }))}
        />
      ) : (
        <GunMilanResults
          result={result}
          boy={boy}
          girl={girl}
          resultsRef={resultsRef}
          onReset={handleReset}
        />
      )}
    </div>
  );
}

function GunMilanForm({
  boy,
  girl,
  loading,
  error,
  onSubmit,
  onBoyChange,
  onGirlChange,
}: {
  boy: FormState;
  girl: FormState;
  loading: boolean;
  error: string | null;
  onSubmit: (event: FormEvent) => void;
  onBoyChange: (patch: Partial<FormState>) => void;
  onGirlChange: (patch: Partial<FormState>) => void;
}) {
  return (
    <form onSubmit={onSubmit} noValidate>
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <PersonPanel label="Boy (वर)" icon="♂" data={boy} onChange={onBoyChange} />
        <PersonPanel label="Girl (वधू)" icon="♀" data={girl} onChange={onGirlChange} />
      </div>

      {error && (
        <div
          role="alert"
          className="mb-4 text-[13px] rounded-lg p-3.5 text-center"
          style={{
            color: "var(--color-error-text)",
            backgroundColor: "var(--color-error-bg)",
            border: "1px solid var(--color-error-tint)",
          }}
        >
          {error}
        </div>
      )}

      <Button
        type="submit"
        onPress={() => {}}
        disabled={loading}
        loading={loading}
        variant="primary"
        title={loading ? "Calculating Gun Milan..." : "Calculate Gun Milan →"}
        className="w-full py-4 text-[15px] font-semibold"
      />

      <p className="mt-3 text-center text-[12px] text-ish-text-faint">
        36-point Ashtakoot score &middot; All 8 Kootas &middot; Mangal Dosha check &middot; 100%
        free
      </p>
    </form>
  );
}

function GunMilanResults({
  result,
  boy,
  girl,
  resultsRef,
  onReset,
}: {
  result: MatchingResponse;
  boy: FormState;
  girl: FormState;
  resultsRef: RefObject<HTMLDivElement | null>;
  onReset: () => void;
}) {
  return (
    <div ref={resultsRef} className="space-y-6">
      <NamesBanner boyName={boy.name} girlName={girl.name} />
      <MatchingScoreCard result={result} />
      <ResultRatingPrompt />
      <KootaBreakdown result={result} />
      <MangalDoshaStatus result={result} />
      <MarriageReportOffer boy={boy} girl={girl} />
      <MatchingCrossSell result={result} />
      <AIAstrologerCTA context="matching" />
      <Recommendation recommendation={result.recommendation} />
      {result.match_id ? (
        <>
          <SavedLinkCard
            path={`/match/${result.match_id}`}
            heading="This match is saved"
            body="Share the link with your family or return to it anytime — no need to re-enter both birth details."
            shareLabel="Share link"
            copiedLabel="Link copied"
            analyticsEvent="match_share_clicked"
            analyticsProps={{ match_id: result.match_id, language: "en", surface: "gun-milan" }}
          />
          <ValueMomentContactCapture
            target={{ type: "match", id: result.match_id }}
            language="en"
            placement="gun-milan-tool-value-moment"
          />
        </>
      ) : null}
      <ResultActions onReset={onReset} />
    </div>
  );
}

function NamesBanner({ boyName, girlName }: { boyName: string; girlName: string }) {
  return (
    <div
      className="card-sacred p-4 flex items-center justify-center gap-3 text-center"
      style={{ borderColor: "var(--color-accent-border)" }}
    >
      <span className="text-[16px] font-bold text-ish-dark">{boyName}</span>
      <span style={{ color: "var(--ish-accent)" }}>♥</span>
      <span className="text-[16px] font-bold text-ish-dark">{girlName}</span>
    </div>
  );
}

function ResultRatingPrompt() {
  return (
    <div style={{ marginTop: 24 }}>
      <RatingPrompt
        source={"tool_result" as never}
        mode="thumbs+text"
        prompt="Was this Kundali Milan score accurate?"
        context={{
          surface: "gun_milan_result",
          path: typeof window !== "undefined" ? window.location.pathname : null,
        }}
      />
    </div>
  );
}

function MarriageReportOffer({ boy, girl }: { boy: FormState; girl: FormState }) {
  // rule 46/47: the gun-milan result MUST emit a fire-once offer-seen impression
  // so this value moment is visible to monetization analytics. PaidReportCTA does
  // not emit it internally; the shared MatchingResultsPanel does (this mirrors
  // MatchingResultsPanel.tsx:176). Stable string key → fires once per presentation.
  const ready = Boolean(boy.birthDate && girl.birthDate);
  useImpressionOnce(ready ? "marriage_report|gun_milan|/gun-milan" : null, () => {
    trackAgentRevenueOfferShown({
      offer_type: "report",
      plan_id: "marriage_report",
      source_path: "/gun-milan",
      intent: "chart_report",
      placement: "gun_milan_results_primary",
      checkout_channel: "ishvaram_report",
      commerce_system: "ishvaram",
      customer_problem: "marriage",
      urgency: "high",
      route_rank_score: 95,
      route_rank_reason: "matching_result_marriage_report",
      ranked_offer_order: ["marriage_report"],
    });
  });
  return (
    <PaidReportCTA
      planId="marriage_report"
      priceInr={99}
      headline="Get the ₹99 Marriage Matching Report"
      subhead="Family-shareable PDF with full Gun Milan, Mangal Dosha, D9/Navamsha compatibility, 5-year dasha timing, risks, strengths, and practical remedies."
      source="gun-milan"
      analyticsContext={{
        placement: "gun_milan_results_primary",
        checkoutChannel: "ishvaram_report",
        commerceSystem: "ishvaram",
        intent: "chart_report",
        sourcePath: "/gun-milan",
        customerProblem: "marriage",
        urgency: "high",
        confidence: 0.9,
        routeRankScore: 95,
        routeRankReason: "matching_result_marriage_report",
        rankedOfferOrder: ["marriage_report"],
      }}
      reportInputs={{
        person_a: {
          name: boy.name,
          birth_date: boy.birthDate,
          birth_time: boy.birthTime || null,
          birth_place: boy.birthPlace,
        },
        person_b: {
          name: girl.name,
          birth_date: girl.birthDate,
          birth_time: girl.birthTime || null,
          birth_place: girl.birthPlace,
        },
        language: "en",
      }}
    />
  );
}

function Recommendation({ recommendation }: { recommendation?: string | null }) {
  if (!recommendation) return null;
  return (
    <div
      className="card-sacred p-6 sm:p-8"
      style={{ backgroundColor: "var(--color-accent-light)" }}
    >
      <h2 className="text-[16px] font-bold mb-3 text-ish-accent">Recommendation</h2>
      <p className="text-[14px] leading-relaxed text-ish-text">{recommendation}</p>
    </div>
  );
}

function ResultActions({ onReset }: { onReset: () => void }) {
  return (
    <div className="flex flex-col sm:flex-row gap-3 justify-center">
      <Link
        href="/kundli-matching/"
        className="btn-sacred text-center font-semibold py-3 px-6 rounded-lg text-[14px]"
      >
        Full Kundli Matching Report →
      </Link>
      <button
        onClick={onReset}
        className="text-center font-semibold py-3 px-6 rounded-lg text-[14px] transition-all duration-200"
        style={{
          color: "var(--ish-text-muted)",
          border: "1px solid var(--ish-border)",
        }}
      >
        Match Another Pair
      </button>
    </div>
  );
}
